import React, { useState, useContext, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import * as motion from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { Search, X, TrendingUp, Sparkles, AlertCircle, Smartphone } from 'lucide-react';
import { AppContext } from '../context/AppContext';
import { PhoneModel, Brand } from '../types';
import ModelImage from './ModelImage';
import BrandLogo from './BrandLogo';
import { presentRepairPrice } from '../utils/repairPrice';
import { searchGlobalModels } from '../domain/globalModelSearch';

interface GlobalModelSearchProps {
    className?: string;
    onSelect?: () => void;
}

const GlobalModelSearch: React.FC<GlobalModelSearchProps> = ({ className = '', onSelect }) => {
    const context = useContext(AppContext);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const overlayInputRef = useRef<HTMLInputElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    // Prevent background scrolling when search overlay is active
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle Escape key to close modal
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Autofocus input when modal opens
    useEffect(() => {
        if (isOpen) {
            // Focus immediately to capture synchronous touch-event stack on mobile
            overlayInputRef.current?.focus();
            
            // Backup short delay in case of paint cycle
            const timer = setTimeout(() => {
                overlayInputRef.current?.focus();
            }, 30);
            return () => clearTimeout(timer);
        } else {
            setSearchTerm('');
        }
    }, [isOpen]);

    const repairData = context?.repairData;

    // Search logic across all models in all brands
    const allModels = React.useMemo(() => {
        const models: { model: PhoneModel, brand: Brand }[] = [];
        (repairData || []).forEach(brand => {
            if (brand.models) {
                brand.models.forEach(model => {
                    if (model.isActive !== false) {
                        models.push({ model, brand });
                    }
                });
            }
        });
        return models;
    }, [repairData]);

    const searchResults = React.useMemo(() => {
        return searchGlobalModels(allModels, searchTerm);
    }, [searchTerm, allModels]);

    // Popular suggestions
    const popularSuggestions = React.useMemo(() => {
        const list: { brandName: string, name: string, model: PhoneModel, brand: Brand }[] = [];
        const currentData = repairData || [];
        
        // Pull some popular ones if they match
        currentData.forEach(brand => {
            if (brand.models) {
                brand.models.forEach(model => {
                    const lName = model.name.toLowerCase();
                    if (
                        lName.includes('15 pro') ||
                        lName.includes('14 pro') ||
                        lName.includes('s24 ultra') ||
                        lName.includes('s23 ultra') ||
                        lName.includes('ipad air')
                    ) {
                        list.push({ brandName: brand.name, name: model.name, model, brand });
                    }
                });
            }
        });

        // Fallback to first few if none found with criteria
        if (list.length === 0) {
            currentData.slice(0, 2).forEach(brand => {
                if (brand.models) {
                    brand.models.slice(0, 3).forEach(model => {
                        list.push({ brandName: brand.name, name: model.name, model, brand });
                    });
                }
            });
        }

        return list.slice(0, 6);
    }, [repairData]);

    if (!context) return null;

    const { navigateToEntity, notify } = context;

    const handleSelectModel = (brand: Brand, model: PhoneModel) => {
        setIsOpen(false);
        if (onSelect) onSelect();
        void navigateToEntity('REPAIR_MODEL', {
            brandId: brand.id,
            modelId: model.id,
        }, 'assign').catch(() => {
            notify('Impossible d’ouvrir ce modèle.', 'error', 'Veuillez réessayer dans quelques instants.');
        });
    };

    return (
        <>
            {/* Inline CSS Keyframes injected for premium high-fidelity rotating halo border and pulsing glow */}
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes html-spin-halo {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes html-breath-glow {
                    0%, 100% {
                        opacity: 0.58;
                        transform: scale(0.985);
                    }
                    50% {
                        opacity: 0.92;
                        transform: scale(1.035);
                    }
                }
                .search-premium-glow {
                    animation: html-breath-glow 3.5s ease-in-out infinite;
                    will-change: opacity, transform;
                }
                @media (prefers-reduced-motion: reduce) {
                    .search-premium-glow {
                        animation: none;
                    }
                }
            `}} />

            {/* The Trigger Bar - Styled with a rotating warm blue halo border and a breathing light glow to draw the custom click */}
            <div 
                onClick={() => setIsOpen(true)}
                className={`relative group flex items-center p-[2.5px] rounded-full transition-all duration-350 cursor-pointer overflow-hidden ${className}`}
                id="global-search-trigger"
                style={{ touchAction: 'manipulation' }}
            >
                {/* Secondary Soft Outer Ambient Glow */}
                <div className="absolute inset-[-4px] rounded-full bg-gradient-to-r from-blue-500/30 via-cyan-400/20 to-indigo-500/30 blur-md pointer-events-none group-hover:scale-105 transition-transform duration-500 opacity-80 search-premium-glow" />

                {/* Main Spinning Conic Gradient Halo (Peeks around the white content container) */}
                <div 
                    className="absolute w-[200%] h-[200%] top-[-50%] left-[-50%] bg-[conic-gradient(from_0deg,#1d4ed8,#06b6d4,#2563eb,#0891b2,#1d4ed8)] pointer-events-none opacity-85"
                    style={{ animation: 'html-spin-halo 6s linear infinite' }}
                />

                {/* Main White Inner Container */}
                <div className="relative w-full h-full flex items-center bg-white rounded-full z-10 pl-4 py-1 pr-2">
                    <span className="text-blue-500 shrink-0 mr-3 animate-pulse">
                        <Search className="w-5 h-5 font-black" />
                    </span>
                    <div className="w-full bg-transparent py-3 md:py-3.5 font-bold text-sm md:text-base text-gray-600 rounded-full cursor-pointer select-none truncate">
                        Chercher un modèle, ex: iPhone 14...
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5 bg-[#f0f9ff] text-blue-600 border border-blue-100/50 rounded-full px-4 py-2 font-black text-xs uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all duration-300">
                        <Sparkles className="w-5 h-5 shrink-0 animate-spin-slow" />
                        <span className="hidden sm:inline">Chercher</span>
                    </div>
                </div>
            </div>

            {/* Premium Fullscreen/Floating Search Overlay */}
            {typeof document !== 'undefined' && createPortal(
            <AnimatePresence>
                {isOpen && (
                    <div className="fixed inset-0 z-[10000] flex items-stretch justify-stretch p-0 sm:items-center sm:justify-center sm:p-4 md:p-8" id="search-modal-portal">
                        {/* Backdrop Blur - optimized to avoid blur on mobile for 60fps performance */}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="absolute inset-0 bg-[#050510]/95 md:bg-[#050510]/85 backdrop-blur-none md:backdrop-blur-md"
                            onClick={() => setIsOpen(false)}
                        />

                        {/* Search Window Container - speeded up animation */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.98, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.98, y: -10 }}
                            transition={{ duration: 0.15, ease: "easeOut" }}
                            ref={modalRef}
                            className="relative w-full h-full h-[100dvh] max-h-[100dvh] min-h-0 bg-white shadow-2xl flex flex-col overflow-hidden border border-gray-100 rounded-none sm:h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-3xl md:h-auto md:max-h-[80dvh] md:max-w-3xl"
                        >
                            {/* Sticky Modal Header */}
                            <div className="flex shrink-0 items-center justify-between border-b border-gray-100 p-3 sm:p-4 md:p-6 bg-white gap-2 sm:gap-4">
                                <div className="relative flex-grow flex items-center bg-gray-50 border border-gray-100 rounded-2xl p-1">
                                    <span className="absolute left-3 sm:left-4 text-gray-500">
                                        <Search className="w-5 h-5" />
                                    </span>
                                    <input
                                        ref={overlayInputRef}
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Taper pour rechercher (ex: iPhone, Samsung, Pro...)"
                                        className="w-full bg-transparent border-none pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 md:py-3.5 font-bold text-[16px] sm:text-sm md:text-base text-gray-900 outline-none placeholder:text-gray-400"
                                    />
                                    {searchTerm && (
                                        <button 
                                            onClick={() => setSearchTerm('')}
                                            className="absolute right-3 sm:right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                                            aria-label="Effacer la recherche"
                                        >
                                            <X className="w-5 h-5 shrink-0" />
                                        </button>
                                    )}
                                </div>
                                
                                <button 
                                    onClick={() => setIsOpen(false)}
                                    className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-750 font-black text-sm transition-all shrink-0 flex items-center justify-center gap-1 min-w-[44px] min-h-[44px]"
                                    aria-label="Fermer la recherche"
                                >
                                    <X className="w-5 h-5 md:w-4 md:h-4 text-black" />
                                    <span className="hidden md:inline text-black font-extrabold uppercase tracking-wide">Fermer</span>
                                </button>
                            </div>

                            {/* Modal Body / Scroll Area */}
                            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4 md:p-6 space-y-5 md:space-y-6">
                                {searchTerm.trim().length === 0 ? (
                                    /* Welcome and Popular Suggestions Mode */
                                    <div className="space-y-5 md:space-y-6">
                                        {/* Browse visually by Brand */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-3 text-blue-600">
                                                <Smartphone className="w-5 h-5 font-black" />
                                                <h4 className="text-xs md:text-sm font-black uppercase tracking-widest text-[#1e1b4b]">Recherche par marque</h4>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                                                {(repairData || []).slice(0, 8).map((brand, bIdx) => (
                                                    <button
                                                        key={`search-brand-${brand.id || bIdx}-${bIdx}`}
                                                        onClick={() => setSearchTerm(brand.name)}
                                                        className="flex min-w-0 items-center justify-center p-2.5 sm:p-3.5 bg-gray-50 border border-gray-100 hover:border-blue-500 hover:bg-blue-50/30 rounded-xl sm:rounded-2xl transition-all duration-300 group min-h-[50px] gap-2 font-bold text-sm text-gray-800"
                                                    >
                                                        <div className="h-6 w-16 flex items-center justify-center text-slate-900 group-hover:text-blue-600 transition-colors">
                                                            <BrandLogo name={brand.name} id={brand.id} className="text-slate-900 dark:text-slate-900 group-hover:text-blue-600" />
                                                        </div>
                                                        <span className="sr-only">{brand.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex items-center gap-2 mb-3 text-blue-600">
                                                <TrendingUp className="w-5 h-5 font-black" />
                                                <h4 className="text-xs md:text-sm font-black uppercase tracking-widest text-[#1e1b4b]">Recherches populaires</h4>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                                {popularSuggestions.map((item, id) => (
                                                    <button
                                                        key={id}
                                                        onClick={() => handleSelectModel(item.brand, item.model)}
                                                        className="flex min-w-0 items-center p-2.5 sm:p-3 text-left bg-gray-50 border border-gray-100 hover:border-blue-500 hover:bg-blue-50/30 rounded-xl sm:rounded-2xl transition-all duration-300 group min-h-[44px]"
                                                    >
                                                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white p-1 border border-gray-150 mr-3 flex items-center justify-center shrink-0 overflow-hidden">
                                                            <ModelImage 
                                                                src={item.model.image} 
                                                                id={item.model.id}
                                                                modelName={item.model.name}
                                                                className="w-full h-full object-contain" 
                                                                loading="eager"
                                                                noPadding={true}
                                                            />
                                                        </div>
                                                        <div className="flex min-w-0 flex-col">
                                                            <span className="text-[10px] font-black uppercase tracking-wider text-blue-600">{item.brandName}</span>
                                                            <span className="text-sm font-black leading-tight text-black break-words group-hover:text-blue-600 transition-colors">{item.name}</span>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="rounded-2xl bg-cyan-50/50 border border-cyan-100 p-3 sm:p-4 flex gap-3 text-cyan-800">
                                            <Sparkles className="w-5 h-5 shrink-0 text-cyan-600 mt-1" />
                                            <div>
                                                <p className="text-sm font-black uppercase tracking-wider mb-1">Assistance Technique Mobilbo</p>
                                                <p className="text-xs leading-relaxed font-bold">Sélectionnez votre appareil pour consulter les services, tarifs et délais actuellement renseignés.</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Search Results View */
                                    <div>
                                        {searchResults.length > 0 ? (
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className="text-xs font-black uppercase tracking-widest text-neutral-400">Modèles correspondants ({searchResults.length})</span>
                                                </div>
                                                <div className="space-y-3">
                                                    {searchResults.map((res, i) => (
                                                        <button
                                                            key={i}
                                                            onClick={() => handleSelectModel(res.brand, res.model)}
                                                            className="w-full flex flex-row items-center p-3 sm:p-4 bg-gray-50 border border-gray-100 rounded-2xl hover:border-blue-500 hover:bg-blue-50/30 transition-all duration-300 text-left gap-3 sm:gap-4 min-h-[44px] group"
                                                        >
                                                            {/* Device Image */}
                                                            <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-2xl bg-white p-1 md:p-1.5 border border-indigo-100 shadow-inner shrink-0 flex items-center justify-center overflow-hidden hover:scale-105 transition-transform duration-300">
                                                                <ModelImage 
                                                                    src={res.model.image} 
                                                                    id={res.model.id}
                                                                    modelName={res.model.name}
                                                                    className="w-full h-full object-contain" 
                                                                    loading="eager"
                                                                    noPadding={true}
                                                                />
                                                            </div>
                                                            
                                                            {/* Device Info */}
                                                            <div className="min-w-0 flex-grow flex flex-col justify-center">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-100/50 px-2.5 py-0.5 rounded-full">{res.brand.name}</span>
                                                                    <span className="font-extrabold text-[#111827] text-sm sm:text-base leading-tight break-words group-hover:text-blue-600 transition-colors">{res.model.name}</span>
                                                                </div>
                                                                
                                                                {/* Popular Repairs list inside result block */}
                                                                {res.model.repairs && res.model.repairs.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                                                        {res.model.repairs.slice(0, 3).map((repair, idx) => (
                                                                            <span key={`search-res-repair-${repair.id || idx}-${idx}`} className="inline-flex items-center bg-white text-gray-700 text-[11px] px-2.5 py-1 rounded-lg font-bold border border-gray-100 shadow-sm">
                                                                                {repair.name} <span className="text-blue-600 ml-1">{presentRepairPrice(repair)}</span>
                                                                            </span>
                                                                        ))}
                                                                        {res.model.repairs.length > 3 && (
                                                                            <span className="inline-flex items-center bg-gray-100 text-gray-650 text-[10px] px-2.5 py-1 rounded-lg font-extrabold border border-gray-200">
                                                                                +{res.model.repairs.length - 3} prestations
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Select Icon */}
                                                            <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-150 text-gray-400 group-hover:text-blue-600 group-hover:border-blue-500 transition-all duration-300">
                                                                <Search className="w-4 h-4" />
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            /* No Results mode */
                                            <div className="p-8 text-center bg-gray-50 rounded-2xl border border-gray-100 flex flex-col items-center justify-center">
                                                <AlertCircle className="w-10 h-10 text-red-500 mb-2 opacity-80 animate-pulse" />
                                                <span className="text-base font-black text-gray-900 mb-1">Aucun modèle trouvé</span>
                                                <p className="text-xs md:text-sm font-semibold text-gray-500 max-w-sm">Désolé, nous n'avons aucun appareil correspondant à "{searchTerm}". Veuillez vérifier l'orthographe ou essayer une autre abréviation.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>,
            document.body
            )}
        </>
    );
};

export default GlobalModelSearch;
